<script lang="ts" setup>
import { proximityScoreMap, segmentation } from '@thecompaniesapi/shared/client'
import { slugify } from '@thecompaniesapi/string-helpers'
import type SegmentationSuggestionAutocomplete from './SegmentationSuggestionAutocomplete.vue'
import { getSignsForAttribute } from '~/composables/useUtils'

const props = defineProps({
  allowLastConditionDeletion: {
    default: true,
    type: Boolean,
  },
  attribute: {
    required: true,
    type: String as PropType<SegmentationAttribute>,
  },
  attributesToExclude: {
    default: () => [],
    type: Array as PropType<SegmentationAttribute[]>,
  },
  blockedOperator: {
    default: false,
    type: Boolean,
  },
  companies: {
    default: () => [],
    type: Array as PropType<CompanyV2[]>,
  },
  conditionIndex: {
    default: 0,
    type: Number,
  },
  index: {
    default: 0,
    type: Number,
  },
  isPreview: {
    default: false,
    type: Boolean,
  },
  isRemovable: {
    default: true,
    type: Boolean,
  },
  operator: {
    required: true,
    type: String as PropType<SegmentationOperator>,
  },
  sign: {
    required: true,
    type: String as PropType<SegmentationSign>,
  },
  updateAiOnType: {
    default: false,
    type: Boolean,
  },
  values: {
    default: () => [],
    type: Array as PropType<SegmentationValues>,
  },
})

const emit = defineEmits<{
  conditionConfirmed: [condition: SegmentationCondition]
  conditionRemoved: [conditionIndex: number]
  conditionUpdated: [condition: SegmentationCondition, conditionIndex: number]
}>()

const { createToast } = useToasts()
const { getIndexForAttribute, getOptionsForAttributes } = useSegmentation()
const { t } = useI18n()
const suggestionsStore = useSuggestionsStore()
const { slideovers } = useOverlays()

const autocompleteRef = ref<InstanceType<typeof SegmentationSuggestionAutocomplete>>()
const suggestionQuery = ref<string>('')
const suggestions = ref<UInputMenuOption[]>([])
const aiSearchQuery = ref(props.attribute === 'ai.search' ? props.values[0] : '')
const metaScore = ref(props.attribute === 'meta.score' ? props.values[0] : '')
const metaSyncedAt = ref(props.attribute === 'meta.syncedAt' ? props.values[0] : '')
const yearFounded = ref(props.attribute === 'about.yearFounded' ? props.values[0] : '')
const maxValues = ref(25)

const aiSearchScore = ref(
  props.attribute === 'ai.search'
    ? props?.values?.[1] ? Number.parseFloat(String(props?.values?.[1])) : 0.50
    : 0.5,
)

const currentAttribute = computed(() => {
  return optionsForAttributes.value.find((option) => {
    return option.value === props.attribute
  })
})

const currentBadges = computed(() => {
  return getBadgesForAttributes({
    attribute: props.attribute,
    suggestions: suggestions.value,
    t,
    values: props.values as string[],
  })
})

const currentSign = computed(() => {
  return optionsForSigns.value.find((option) => {
    return option.value === props.sign
  })
})

const optionsForAttributes = computed(() => {
  return getOptionsForAttributes(props.attributesToExclude)
})

const optionsForSigns = computed(() => {
  return getSignsForAttribute(props.attribute, t)
})

watch(
  props.companies,
  () => {
    autocompleteRef.value?.generateSuggestions()
  },
)

// Watch keyword query to search on change
watch([aiSearchQuery, aiSearchScore], () => {
  if (props.updateAiOnType) {
    onSearchWithAI()
  }
})

// Watch local values to toggle them on change
watch(
  [
    yearFounded,
    metaScore,
    metaSyncedAt,
  ],
  () => {
    if (props.attribute === 'about.yearFounded') {
      toggleValue(String(yearFounded.value))
    }
    if (props.attribute === 'meta.score') {
      toggleValue(String(metaScore.value))
    }
    if (props.attribute === 'meta.syncedAt') {
      toggleValue(String(metaSyncedAt.value))
    }
  },
  { immediate: true },
)

watch(
  () => props.attribute,
  async (newVal) => {
    // Default value for meta.syncedAt
    if (newVal === 'meta.syncedAt') {
      metaSyncedAt.value = props?.values?.[0] || 'last365days'
    }

    // Default values for meta.score and about.yearFounded
    if (newVal === 'meta.score') {
      metaScore.value = props?.values?.[0] || '50'
    }

    // Default value for about.yearFounded
    if (newVal === 'about.yearFounded') {
      yearFounded.value = props?.values?.[0] || '2025'
    }
  },
  { immediate: true },
)

function toggleValue(value: string) {
  // Clean value
  const cleanedValue = `${value}`.toLowerCase().trim().replace(/'|"/gi, '').replace(/\//g, '-')

  // Support values that support a single value
  if (props.attribute === 'meta.score' || props.attribute === 'meta.syncedAt' || props.attribute === 'about.yearFounded') {
    const condition: SegmentationCondition = {
      attribute: props.attribute,
      blockedOperator: props.blockedOperator,
      operator: props.operator,
      sign: props.sign,
      values: [cleanedValue],
    }

    emit('conditionUpdated', condition, props.conditionIndex)
  }
  // Warn when maximum values reached
  else if (props.values.length >= maxValues.value) {
    createToast({
      i18nParams: { max: maxValues.value },
      message: 'maxValuesReached',
      status: 'warning',
    })
  }
  // Push value if not already defined
  else if (!props.values.includes(cleanedValue)) {
    suggestionQuery.value = ''

    const condition: SegmentationCondition = {
      attribute: props.attribute,
      blockedOperator: props.blockedOperator,
      operator: props.operator,
      sign: props.sign,
      values: [...props.values, cleanedValue],
    }

    emit('conditionUpdated', condition, props.conditionIndex)
    updateUsageCount(cleanedValue)
    autocompleteRef.value?.generateSuggestions()
  }
  // Remove value if already defined
  else {
    const condition: SegmentationCondition = {
      attribute: props.attribute,
      blockedOperator: props.blockedOperator,
      operator: props.operator,
      sign: props.sign,
      values: props.values.filter(currentValue => currentValue !== cleanedValue),
    }

    emit('conditionUpdated', condition, props.conditionIndex)
    autocompleteRef.value?.generateSuggestions()
  }
}

function updateUsageCount(suggestionCode: string) {
  if ([
    'about.industries',
    'locations.headquarters.city.code',
    'locations.headquarters.continent.code',
    'locations.headquarters.country.code',
    'locations.headquarters.county.code',
    'locations.headquarters.state.code',
    'technologies.active',
  ].includes(props.attribute)) {
    const index = getIndexForAttribute(props.attribute)

    if (index) {
      suggestionsStore.updateSuggestion({
        index: index as SuggestionsUpdateIndices,
        suggestionCode,
      })
    }
  }
}

function onAttributeChanged(option: USelectMenuOption) {
  const condition: SegmentationCondition = {
    attribute: option.value as SegmentationAttribute,
    blockedOperator: segmentation.attributesWithBlockedOperator.includes(option.value),
    operator: 'or',
    sign: 'equals',
    values: [],
  }

  // Clear query and values when attribute is changed
  suggestionQuery.value = ''

  emit('conditionUpdated', condition, props.conditionIndex)

  autocompleteRef.value?.generateSuggestions()
}

function onBadgeRemoved(badge: UBadge) {
  const condition: SegmentationCondition = {
    attribute: props.attribute,
    blockedOperator: props.blockedOperator,
    operator: props.operator,
    sign: props.sign,
    values: props.values.filter(value => value !== badge.value),
  }

  emit('conditionUpdated', condition, props.conditionIndex)
}

function onBadgeUpdated(badge: UBadge) {
  const condition: SegmentationCondition = {
    attribute: props.attribute,
    blockedOperator: props.blockedOperator,
    operator: props.operator,
    sign: props.sign,
    values: props.values.map((value) => {
      const badgeName = badge.value?.split('@')[0]
      const badgeNewVersion = badge.menu?.value?.replaceAll(' ', '') || ''
      const badgeOldVersion = badge.value?.split('@')[1]
      const valueName = String(value).split('@')[0]

      if (
        // Attribute does not support versioning
        !String(value).includes('@')
        // Or is not the same value
        || valueName !== badgeName
        // Or the version has not changed in the model
        || badgeNewVersion === badgeOldVersion
      ) {
        return value
      }

      return `${badgeName}@${badgeNewVersion}`
    }),
  }

  emit('conditionUpdated', condition, props.conditionIndex)
}

function onConditionRemoved() {
  emit('conditionRemoved', props.conditionIndex)
}

function onOperatorClicked() {
  const condition: SegmentationCondition = {
    attribute: props.attribute,
    blockedOperator: props.blockedOperator,
    operator: props.operator === 'and' ? 'or' : 'and',
    sign: props.sign,
    values: props.values,
  }

  // Make sure operator is not blocked
  if (!props.blockedOperator) {
    if (props.operator === 'and') {
      emit('conditionUpdated', condition, props.conditionIndex)
    }
    else if (props.operator === 'or') {
      emit('conditionUpdated', condition, props.conditionIndex)
    }
  }
}

function onSearchWithAI() {
  const condition: SegmentationCondition = {
    attribute: props.attribute,
    blockedOperator: props.blockedOperator,
    operator: props.operator,
    sign: props.sign,
    values: aiSearchQuery.value ? [String(aiSearchQuery.value).trim(), aiSearchScore.value.toString()] : [],
  }

  emit('conditionConfirmed', condition)
}

function onSignChanged(option: USelectMenuOption) {
  const condition: SegmentationCondition = {
    attribute: props.attribute,
    blockedOperator: props.blockedOperator,
    operator: props.operator,
    sign: option.value,
    values: props.values,
  }

  emit('conditionUpdated', condition, props.conditionIndex)
}

function onSuggestionSelected(option: UInputMenuOption) {
  if (option.versionDetected) {
    option.value = `${option.value}@`
  }

  toggleValue(option.value)
}

function onSubmitSuggestionQuery() {
  if (suggestionQuery.value) {
    toggleValue(slugify(suggestionQuery.value))
  }
}

function onToggleSuggestion(value: string) {
  toggleValue(value)
}
</script>

<template>
  <div class="flex w-full text-gray-900 dark:text-white">
    <UTooltip
      v-if="!isPreview && isRemovable"
      class="mr-2.5 h-fit flex-initial"
      :popper="{ placement: 'bottom' }"
      :text="t('globals.remove')"
    >
      <UButton
        class="mt-1.5 cursor-pointer self-start outline-none hover:text-gray-900 focus-visible:text-gray-900 dark:hover:text-white"
        :disabled="!allowLastConditionDeletion && index === 0"
        :icon="getIcon('trash')"
        size="xs"
        :ui="{ icon: { base: 'text-red-600 dark:text-red-400' } }"
        @click="onConditionRemoved"
      />
    </UTooltip>

    <div class="flex flex-1 flex-col gap-1.5">
      <div class="flex items-center gap-2">
        <USelectMenu
          class="min-w-48 flex-1"
          :model-value="attribute"
          :options="optionsForAttributes"
          searchable
          :searchable-placeholder="t('placeholders.selectMenu')"
          size="md"
          @change="onAttributeChanged"
        >
          <template #label>
            <div class="flex min-w-0 items-center gap-1.5">
              <UIcon
                v-if="currentAttribute?.icon"
                class="size-5 flex-shrink-0"
                :name="currentAttribute.icon"
              />

              <span class="truncate">{{ currentAttribute?.label }}</span>
            </div>
          </template>

          <template #option="{ option }">
            <div class="flex min-w-0 items-center gap-1.5">
              <UIcon
                v-if="option?.icon"
                class="size-5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                :name="option.icon"
              />

              <span class="truncate">{{ option?.label }}</span>
            </div>
          </template>

          <template #option-empty="{ query }">
            {{ t('globals.noResultsFor', { query }) }}
          </template>
        </USelectMenu>

        <USelectMenu
          class="flex-initial sm:w-44"
          :model-value="sign"
          :options="optionsForSigns"
          size="md"
          @change="onSignChanged"
        >
          <template #label>
            <div
              v-if="currentSign"
              class="flex items-center"
            >
              <UIcon
                class="mr-1.5 size-5"
                :name="currentSign.icon"
              />

              <span class="hidden sm:inline-flex">
                {{ currentSign.label }}
              </span>
            </div>
          </template>
        </USelectMenu>
      </div>

      <template v-if="attribute === 'meta.score'">
        <div class="flex w-full flex-col items-center">
          <div class="relative mt-2 w-full">
            <UInput
              v-model="metaScore"
              class="w-full"
              :placeholder="t('placeholders.score')"
            />
          </div>
        </div>
      </template>

      <template v-else-if="attribute === 'about.yearFounded'">
        <div class="flex w-full flex-col items-center">
          <div class="relative mt-2 w-full">
            <UInput
              v-model="yearFounded"
              class="w-full"
              :placeholder="t('placeholders.yearFounded')"
            />
          </div>
        </div>
      </template>

      <template v-else-if="attribute === 'ai.search'">
        <div class="flex w-full flex-col items-center gap-4">
          <div class="relative w-full">
            <UTextarea
              v-model="aiSearchQuery"
              autoresize
              class="my-2 w-full"
              :placeholder="t('placeholders.aiSearch')"
              :rows="4"
              :ui="{ padding: { sm: 'pb-9' } }"
            />

            <div class="absolute bottom-3 right-0">
              <UButton
                v-if="aiSearchScore === proximityScoreMap.far || !aiSearchScore"
                color="purple"
                :icon="getIcon('similarityLow')"
                :label="t('globals.similarityLow')"
                size="xs"
                trailing
                variant="link"
                @click="aiSearchScore = proximityScoreMap.near"
              />

              <UButton
                v-else-if="aiSearchScore === proximityScoreMap.near"
                color="violet"
                :icon="getIcon('similarityMedium')"
                :label="t('globals.similarityMedium')"
                size="xs"
                trailing
                variant="link"
                @click="aiSearchScore = proximityScoreMap.close"
              />

              <UButton
                v-else-if="aiSearchScore === proximityScoreMap.close"
                color="indigo"
                :icon="getIcon('similarityHigh')"
                :label="t('globals.similarityHigh')"
                size="xs"
                trailing
                variant="link"
                @click="aiSearchScore = proximityScoreMap.far"
              />
            </div>
          </div>

          <UButton
            v-if="!updateAiOnType && aiSearchQuery && (aiSearchQuery !== values[0] || aiSearchScore !== Number.parseFloat(String(values[1])))"
            block
            :icon="getIcon('ai')"
            :label="t('searchCompaniesWithAI')"
            size="md"
            variant="subtle"
            @click="onSearchWithAI"
          />
        </div>
      </template>

      <template v-else>
        <SegmentationSuggestionAutocomplete
          ref="autocompleteRef"
          v-model:query="suggestionQuery"
          v-model:suggestions="suggestions"
          :attribute="attribute"
          :companies="companies"
          :current-values="currentBadges.map(badge => badge.value)"
          :is-preview="isPreview"
          @submit-suggestion-query="onSubmitSuggestionQuery"
          @suggestion-selected="onSuggestionSelected"
        />

        <BaseBadges
          v-if="currentBadges.length"
          :badges="currentBadges"
          :blocked-operator="blockedOperator"
          :class="{ 'mt-3': !isPreview }"
          :clickable="!isPreview"
          :operator="operator"
          :removable="!isPreview"
          size="xs"
          variant="subtle"
          @badge-click="onBadgeRemoved"
          @badge-updated="onBadgeUpdated"
          @operator-click="onOperatorClicked"
        >
          <template #badge-menu="{ badge }">
            <UInput
              v-if="badge.menu"
              v-model="badge.menu.value"
              autofocus
              :ui="{ base: '!px-0.5 w-8' }"
              variant="none"
              @blur="onBadgeUpdated(badge)"
              @keyup.enter="onBadgeUpdated(badge)"
            />
          </template>
        </BaseBadges>
      </template>
    </div>
  </div>

  <SlideoverSuggestions
    v-if="slideovers.suggestions.opened"
    v-model="slideovers.suggestions.opened"
    :attribute="attribute"
    :values="values"
    @toggle-suggestion="onToggleSuggestion"
  />
</template>

<i18n lang="json">
{
  "en": {
    "placeholders": {
      "aiSearch": "Describe the companies you want to find",
      "score": "50",
      "selectMenu": "Search an attribute",
      "yearFounded": "1990"
    },
    "searchCompaniesWithAI": "Search with AI",
    "suggestionsMessage": {
      "keyword": "We accept all keywords (we display just suggestions). Keywords are not case-sensitive or accent-sensitive.",
      "number": "You can type any number that you want (we display just suggestions).",
      "technologies": "Need a specific technology? Contact us and we'll add it."
    }
  },
  "es": {
    "placeholders": {
      "aiSearch": "Describe las empresas que quieres encontrar",
      "score": "50",
      "selectMenu": "Buscar un atributo",
      "yearFounded": "1990"
    },
    "searchCompaniesWithAI": "Buscar con IA",
    "suggestionsMessage": {
      "keyword": "Aceptamos todas las palabras clave (solo mostramos sugerencias). Las palabras clave no distinguen entre mayúsculas y minúsculas o acentos.",
      "number": "Puedes escribir cualquier número que desees (solo mostramos sugerencias).",
      "technologies": "¿Necesitas una tecnología específica? Contacta con nosotros y la añadiremos."
    }
  },
  "fr": {
    "placeholders": {
      "aiSearch": "Décrivez les entreprises que vous souhaitez trouver",
      "score": "50",
      "selectMenu": "Rechercher un attribut",
      "yearFounded": "1990"
    },
    "searchCompaniesWithAI": "Rechercher avec l'IA",
    "suggestionsMessage": {
      "keyword": "Nous acceptons tous les mots-clés (nous affichons juste des suggestions). Les mots-clés ne sont pas sensibles à la casse ou aux accents.",
      "number": "Vous pouvez taper n'importe quel nombre (nous affichons juste des suggestions).",
      "technologies": "Vous cherchez une technologie spécifique ? Contactez-nous et nous l'ajouterons."
    }
  }
}
</i18n>
